/**
 * SHY-1736 — Split Payment guard: one plan at a time.
 *
 * Theme-only safeguard for the Split & Partial Payments 2 app (Spurit / Finloop). While any plan
 * has an unpaid part, Split Payment and checkout are locked, and the cart page shows a panel.
 *
 * Sources of truth
 * - Which plans exist: the app's own localStorage key `ssp2_checkout_invoices`, one entry per plan,
 *   `[{ invoiceUrls: [...] }]`. The app never removes entries, so a finished plan stays there.
 * - Paid or unpaid: a live HEAD check on each invoice URL (same check the app's widget uses).
 *   `opaqueredirect` = unpaid, anything else = paid. Kept in memory only.
 * - Amounts, draft names and cart items: captured from the app's POST /api/v1/splits call and
 *   stored in `vro_split_plans`, keyed by the sorted invoice URLs. Plans without a record
 *   (created before release) are "legacy": shown without amounts, and the cart is never touched.
 *
 * Fail closed: if a plan is in storage the lock is applied straight away and only lifted once the
 * checks show every part paid. A failed check counts as unpaid.
 *
 * Strings come from snippets/vro-split-guard-config.liquid (locales: vorwerk_ui.split_guard_*).
 * CSS lives in assets/jsc-reset-style.css, block "split payment guard".
 * Exposes window.vroSplitGuard = { isLocked(), refresh() }.
 */
(function () {
  'use strict';

  var APP_KEY = 'ssp2_checkout_invoices';
  var LEGACY_ID_KEY = 'ssp2_checkout_id';
  var LEGACY_INVOICES_KEY = 'ssp2_invoice_ids';
  var WIDGET_KEY = 'widget_open';
  var RECORDS_KEY = 'vro_split_plans';
  var DONE_KEY = 'vro_split_done';
  var LOCK_CLASS = 'vro-split-locked';
  var RECORD_GRACE_MS = 60000;
  var POLL_MS = 3000;

  var paid = {}; // invoice url -> true when the last check said paid
  var checksDone = false;
  var checking = null;
  var completing = false;
  var showDone = false;
  var lastAppRaw = null;

  function noop() {}

  // ---------- storage (every access is guarded: private mode or blocked storage must not break the cart) ----------

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) {}
  }

  function lsRemove(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  }

  function readJson(key, fallback) {
    try {
      var v = JSON.parse(lsGet(key));
      return v === null || v === undefined ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }

  function readRecords() {
    var r = readJson(RECORDS_KEY, {});
    return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
  }

  function config() {
    try {
      return JSON.parse(document.getElementById('vro-split-guard-config').textContent) || {};
    } catch (e) {
      return {};
    }
  }

  // ---------- plans ----------

  function planKey(urls) {
    return urls.slice().sort().join('|');
  }

  function isUrlList(urls) {
    return Array.isArray(urls) && urls.length > 0 && urls.every(function (u) { return typeof u === 'string' && u; });
  }

  // Same fallback as the app: the old checkout id + invoice ids keys are only used when the new key is empty.
  function readAppPlans() {
    var raw = readJson(APP_KEY, []);
    var plans = [];
    if (Array.isArray(raw) && raw.length) {
      raw.forEach(function (entry) {
        var urls = entry && entry.invoiceUrls;
        if (isUrlList(urls)) plans.push({ urls: urls, key: planKey(urls), legacyKeys: false });
      });
      return plans;
    }
    var checkoutId = lsGet(LEGACY_ID_KEY);
    var ids = readJson(LEGACY_INVOICES_KEY, []);
    if (checkoutId && Array.isArray(ids) && ids.length) {
      var urls = ids.map(function (id) { return window.location.origin + '/' + checkoutId + '/invoices/' + id; });
      plans.push({ urls: urls, key: planKey(urls), legacyKeys: true });
    }
    return plans;
  }

  function getPlans() {
    var records = readRecords();
    return readAppPlans().map(function (p) {
      p.record = records[p.key] || null;
      return p;
    });
  }

  function currentCustomer() {
    var info = window.theme && window.theme.customerInfo;
    return ((info && info.email) || '').toLowerCase();
  }

  // Open point (needs a decision): a plan only locks the customer who created it. Guest plans and
  // legacy plans (no record) lock everyone, and a logged-out visitor is locked by every plan.
  function appliesToCustomer(plan) {
    var owner = plan.record && plan.record.customer;
    var current = currentCustomer();
    return !owner || !current || owner === current;
  }

  function isComplete(plan) {
    return plan.urls.every(function (u) { return paid[u] === true; });
  }

  function openPlans() {
    return getPlans().filter(function (p) { return appliesToCustomer(p) && !isComplete(p); });
  }

  function isLocked() {
    return openPlans().length > 0;
  }

  // Forget a plan in the app's storage and in ours.
  function forgetPlan(plan) {
    var kept = readJson(APP_KEY, []);
    kept = Array.isArray(kept) ? kept.filter(function (e) {
      return !(e && isUrlList(e.invoiceUrls) && planKey(e.invoiceUrls) === plan.key);
    }) : [];
    lsSet(APP_KEY, JSON.stringify(kept));
    if (plan.legacyKeys) {
      lsRemove(LEGACY_ID_KEY);
      lsRemove(LEGACY_INVOICES_KEY);
    }
    var records = readRecords();
    delete records[plan.key];
    lsSet(RECORDS_KEY, JSON.stringify(records));
    if (!readAppPlans().length) lsRemove(WIDGET_KEY);
  }

  // Records whose plan is no longer in the app's storage (e.g. the shopper cleared it) are dropped.
  function pruneRecords() {
    var records = readRecords();
    var keys = {};
    readAppPlans().forEach(function (p) { keys[p.key] = true; });
    var changed = false;
    Object.keys(records).forEach(function (k) {
      var age = Date.now() - ((records[k] && records[k].created_at) || 0);
      if (!keys[k] && age > RECORD_GRACE_MS) {
        delete records[k];
        changed = true;
      }
    });
    if (changed) lsSet(RECORDS_KEY, JSON.stringify(records));
  }

  // ---------- capturing plan details from the app's request ----------

  function captureSplit(parts, body) {
    try {
      var request = typeof body === 'string' ? JSON.parse(body) : null;
      if (!request || !Array.isArray(parts) || !parts.length) return;
      var urls = parts.map(function (p) { return p && p.invoice_url; });
      if (!isUrlList(urls)) return;
      var cart = request.cart || {};
      var records = readRecords();
      records[planKey(urls)] = {
        created_at: Date.now(),
        customer: currentCustomer(),
        currency: cart.currency || '',
        parts: parts.map(function (p) {
          return { name: p.name || '', draft_id: p.id || '', price: p.price || '', invoice_url: p.invoice_url };
        }),
        items: (cart.items || []).map(function (i) {
          return { variant_id: i.variant_id || i.id, quantity: i.quantity || 1, properties: i.properties || {} };
        })
      };
      lsSet(RECORDS_KEY, JSON.stringify(records));
      // The app writes its own storage a moment later; lock as soon as it has.
      [0, 300, 1000].forEach(function (ms) { setTimeout(refresh, ms); });
    } catch (e) {}
  }

  function installFetchHook() {
    if (!window.fetch || window.fetch.__vroSplitGuard) return;
    var original = window.fetch;
    var wrapped = function (input, init) {
      var promise = original.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (method === 'POST' && /split-payments\.spur-i-t\.com\/api\/v1\/splits(\?|$)/.test(url)) {
          var body = init && init.body;
          promise.then(function (res) {
            if (!res.ok) return null;
            return res.clone().json();
          }).then(function (parts) {
            if (parts) captureSplit(parts, body);
          }).catch(noop);
        }
      } catch (e) {}
      return promise;
    };
    wrapped.__vroSplitGuard = true;
    window.fetch = wrapped;
  }

  // ---------- paid check ----------

  // The invoice link may sit on another host than the storefront (myshopify.com vs the custom
  // domain). A cross-origin HEAD is blocked by the browser, so ask the same path on this origin.
  function sameOriginUrl(url) {
    try {
      var u = new URL(url);
      if (u.origin !== window.location.origin && /^\/\d+\/invoices\//.test(u.pathname)) {
        return window.location.origin + u.pathname + u.search;
      }
    } catch (e) {}
    return url;
  }

  // paid[url]: true = paid, false = checked and unpaid, 'unknown' = the check itself failed.
  function checkUrl(url) {
    return fetch(sameOriginUrl(url), { method: 'HEAD', redirect: 'manual' }).then(function (res) {
      paid[url] = res.type !== 'opaqueredirect';
    }).catch(function () {
      if (paid[url] !== true) paid[url] = 'unknown';
    });
  }

  // Cancel is only offered when every part was checked and is unpaid. If a check failed we cannot
  // prove nothing was paid, so the shopper is sent to customer service instead.
  function canCancel(plan) {
    return plan.urls.every(function (u) { return paid[u] === false; });
  }

  function checkAll() {
    if (checking) return checking;
    var urls = [];
    getPlans().forEach(function (p) {
      p.urls.forEach(function (u) { if (paid[u] !== true && urls.indexOf(u) === -1) urls.push(u); });
    });
    checking = Promise.all(urls.map(checkUrl)).then(function () {
      checksDone = true;
      return completePlans();
    }).then(function () {
      checking = null;
      refresh();
    }, function () {
      checking = null;
      refresh();
    });
    return checking;
  }

  // ---------- completion: every part paid ----------

  // Removes only the plan's items from the cart (never below 0), forgets the plan, flags a confirmation.
  function completePlan(plan) {
    var items = plan.record && plan.record.items;
    var work = Promise.resolve(false);
    if (items && items.length) {
      work = fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
        return r.json();
      }).then(function (cart) {
        var updates = {};
        var any = false;
        items.forEach(function (item) {
          var left = item.quantity;
          cart.items.forEach(function (line) {
            if (line.variant_id !== item.variant_id || left <= 0) return;
            var current = updates[line.key] !== undefined ? updates[line.key] : line.quantity;
            var take = Math.min(current, left);
            if (take > 0) {
              updates[line.key] = current - take;
              left -= take;
              any = true;
            }
          });
        });
        if (!any) return false;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (r) {
          if (!r.ok) throw new Error('cart update failed');
          return true;
        });
      });
    }
    return work.then(function (cartChanged) {
      forgetPlan(plan);
      if (plan.record) {
        try { sessionStorage.setItem(DONE_KEY, '1'); } catch (e) {}
      }
      return cartChanged || !!plan.record;
    });
  }

  function completePlans() {
    if (completing) return Promise.resolve();
    var done = getPlans().filter(function (p) { return appliesToCustomer(p) && isComplete(p); });
    if (!done.length) return Promise.resolve();
    completing = true;
    return Promise.all(done.map(function (p) {
      return completePlan(p).catch(function () { return false; }); // keep the plan, retry on the next check
    })).then(function (results) {
      completing = false;
      if (results.some(Boolean)) window.location.reload();
    });
  }

  // ---------- cancel: no part paid ----------

  function cancelPlan(key) {
    var plan = getPlans().filter(function (p) { return p.key === key; })[0];
    if (!plan) return;
    // Re-check first: a part may have been paid since the page loaded.
    Promise.all(plan.urls.map(checkUrl)).then(function () {
      if (!canCancel(plan)) {
        refresh();
        return;
      }
      forgetPlan(plan);
      // The app's widget only reads storage when it starts, so reload.
      window.location.reload();
    });
  }

  // ---------- panel (cart page) ----------

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function fill(text, vars) {
    return String(text || '').replace(/\[(\w+)\]/g, function (m, name) {
      return vars && vars[name] !== undefined ? vars[name] : m;
    });
  }

  // Same look as the app's own popup and the order totals: "4.287,29 lei".
  function formatPrice(value, currency) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    try {
      if (!currency || currency === 'RON') {
        return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' lei';
      }
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: currency }).format(n);
    } catch (e) {
      return n.toFixed(2) + (currency ? ' ' + currency : '');
    }
  }

  function buildPlan(plan, cfg) {
    var wrap = el('div', 'vro-split-guard__plan');
    // Draft numbers (#D280 · #D281) tell several open plans apart and give support something to quote.
    var refs = plan.record && plan.record.parts ? plan.record.parts.map(function (p) { return p.name; }).filter(Boolean).join(' · ') : '';
    if (refs) wrap.appendChild(el('p', 'vro-split-guard__label', refs));
    wrap.appendChild(el('p', 'vro-split-guard__message', cfg.message));
    var list = el('ul', 'vro-split-guard__parts');
    plan.urls.forEach(function (url, i) {
      var part = plan.record && plan.record.parts && plan.record.parts.filter(function (p) { return p.invoice_url === url; })[0];
      var li = el('li', 'vro-split-guard__part');
      var label = fill(cfg.part, { number: i + 1 });
      var price = part ? formatPrice(part.price, plan.record.currency) : '';
      li.appendChild(el('span', 'vro-split-guard__part-label', price ? label + ' · ' + price : label));
      if (paid[url] === true) {
        li.appendChild(el('span', 'vro-split-guard__paid', cfg.paid));
      } else if (/^https:\/\//.test(url)) {
        var link = el('a', 'btn btn--small vro-split-guard__pay', fill(cfg.payPart, { number: i + 1 }));
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener';
        li.appendChild(link);
      }
      list.appendChild(li);
    });
    wrap.appendChild(list);

    if (canCancel(plan)) {
      var cancel = el('button', 'btn btn--secondary vro-split-guard__cancel', cfg.cancel);
      cancel.type = 'button';
      cancel.addEventListener('click', function () { cancelPlan(plan.key); });
      wrap.appendChild(cancel);
    } else {
      var contactRefs = plan.record && plan.record.parts ? plan.record.parts.map(function (p) { return p.name; }).filter(Boolean).join(', ') : '';
      // "Need help? <Contact customer service> Reference: #D280, #D281"
      var note = el('p', 'vro-split-guard__contact');
      note.appendChild(document.createTextNode(cfg.contactNote + ' '));
      var contact = el('a', '', cfg.contact);
      contact.href = cfg.contactUrl || '/pages/contact-us';
      note.appendChild(contact);
      if (cfg.references && contactRefs) {
        note.appendChild(document.createTextNode(' ' + fill(cfg.references, { references: contactRefs })));
      }
      wrap.appendChild(note);
    }
    return wrap;
  }

  function renderPanel(plans) {
    var box = document.getElementById('vro-split-guard');
    if (!box) return;
    if (!checksDone && !showDone) return; // lock is already on; wait for real paid states before drawing
    var cfg = config();
    var signature = JSON.stringify([showDone, plans.map(function (p) {
      return [p.key, p.urls.map(function (u) { return String(paid[u]); })];
    })]);
    if (box.getAttribute('data-signature') === signature) return;
    box.setAttribute('data-signature', signature);
    while (box.firstChild) box.removeChild(box.firstChild);
    if (showDone) box.appendChild(el('p', 'vro-split-guard__done', cfg.done));
    // The app appends new plans to its list, so reverse it to show the newest plan first.
    plans.slice().reverse().forEach(function (p) { box.appendChild(buildPlan(p, cfg)); });
    box.hidden = !box.firstChild;
  }

  // ---------- app widget: add "cancel" under plans with no payment ----------

  function decorateWidget(plans) {
    if (!checksDone) return;
    var cfg = config();
    var groups = document.querySelectorAll('.spp2__reminder-group-container');
    Array.prototype.forEach.call(groups, function (group) {
      if (group.querySelector('.vro-split-cancel')) return;
      var link = group.querySelector('a[href^="http"]:not(.spp2__reminder-link_disabled)');
      if (!link) return;
      var plan = plans.filter(function (p) { return p.urls.indexOf(link.getAttribute('href')) !== -1; })[0];
      if (!plan || !canCancel(plan)) return;
      var cancel = el('a', 'vro-split-cancel', cfg.cancel);
      cancel.href = '#';
      cancel.addEventListener('click', function (e) {
        e.preventDefault();
        cancelPlan(plan.key);
      });
      group.appendChild(cancel);
    });
  }

  // ---------- blocking ----------

  function setLock(locked) {
    document.documentElement.classList.toggle(LOCK_CLASS, locked);
  }

  function isBlockedTarget(node) {
    if (!node || !node.closest || node.closest('#vro-split-guard')) return false;
    return !!node.closest(
      '.btn-split-payment, #jsc-checkout-btn, .shopify-checkout-btn, .spp2__payment-button,' +
      '.spp2__button-container, [data-spd-checkout-button], button[name="checkout"], input[name="checkout"]'
    );
  }

  function installEventGuards() {
    // Capture phase runs before the inline onclick handlers (splitPayment / checkout).
    document.addEventListener('click', function (e) {
      if (isLocked() && isBlockedTarget(e.target)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);
    document.addEventListener('submit', function (e) {
      if (!isLocked()) return;
      var action = (e.target && e.target.getAttribute && e.target.getAttribute('action')) || '';
      if ((e.submitter && e.submitter.name === 'checkout') || /\/checkout/.test(action)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);
  }

  // ---------- refresh ----------

  function refresh() {
    pruneRecords();
    var plans = openPlans();
    setLock(plans.length > 0);
    renderPanel(plans);
    decorateWidget(plans);
  }

  // Read once per page start, never in refresh(): a refresh can run just before the completion
  // reload and would use up the flag on the page that is about to go away.
  function consumeDoneFlag() {
    try {
      if (sessionStorage.getItem(DONE_KEY) && document.getElementById('vro-split-guard')) {
        showDone = true;
        sessionStorage.removeItem(DONE_KEY);
      }
    } catch (e) {}
  }

  function init() {
    installEventGuards();
    consumeDoneFlag();
    refresh();
    checkAll();
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) checkAll();
    });
    window.addEventListener('focus', function () { checkAll(); });
    window.addEventListener('storage', function () { refresh(); checkAll(); });
    // The app can write its storage after we load; pick up changes without a reload.
    setInterval(function () {
      var raw = lsGet(APP_KEY) + '|' + lsGet(LEGACY_INVOICES_KEY);
      if (raw !== lastAppRaw) {
        lastAppRaw = raw;
        refresh();
        checkAll();
      }
    }, POLL_MS);
    // The cart section and the app's widget re-render themselves; put our parts back.
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(refresh, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.vroSplitGuard = { isLocked: isLocked, refresh: refresh };
  installFetchHook();
  // Fail closed: lock before the DOM is ready if any plan is waiting in storage.
  setLock(isLocked());
  lastAppRaw = lsGet(APP_KEY) + '|' + lsGet(LEGACY_INVOICES_KEY);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
